/**
 * Octane Car Festival Theme - Main JavaScript
 * Handles cart interactions, mobile menu, accordions, and more
 */

// ===========================================
// CART DRAWER FUNCTIONALITY
// ===========================================

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-overlay');
    this.closeButton = document.querySelector('.cart-drawer-close');
    this.cartToggleButtons = document.querySelectorAll('.cart-toggle');

    this.init();
  }

  init() {
    if (!this.drawer) return;

    // Open cart drawer on toggle button click
    this.cartToggleButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close cart drawer on close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Close cart drawer on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close cart drawer on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('active')) {
        this.close();
      }
    });
  }

  open() {
    this.drawer.classList.add('active');
    if (this.overlay) this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('active');
    if (this.overlay) this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  toggle() {
    this.drawer.classList.toggle('active');
    if (this.overlay) this.overlay.classList.toggle('active');
    document.body.style.overflow = this.drawer.classList.contains('active') ? 'hidden' : '';
  }
}

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});

// ===========================================
// MOBILE MENU TOGGLE
// ===========================================

class MobileMenu {
  constructor() {
    this.hamburger = document.querySelector('.hamburger');
    this.nav = document.querySelector('.mobile-nav');

    this.init();
  }

  init() {
    if (!this.hamburger || !this.nav) return;

    this.hamburger.addEventListener('click', () => this.toggle());
  }

  toggle() {
    this.nav.classList.toggle('active');
    this.hamburger.classList.toggle('active');
    document.body.style.overflow = this.nav.classList.contains('active') ? 'hidden' : '';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
});

// ===========================================
// ACCORDION FUNCTIONALITY
// ===========================================

class Accordion {
  constructor(container) {
    this.container = container;
    this.headers = container.querySelectorAll('[data-accordion-header]');

    this.init();
  }

  init() {
    this.headers.forEach(header => {
      header.addEventListener('click', () => this.toggle(header));
    });
  }

  toggle(header) {
    const content = header.nextElementSibling;
    const isOpen = header.classList.contains('active');

    if (isOpen) {
      header.classList.remove('active');
      content.style.maxHeight = '0';
    } else {
      header.classList.add('active');
      content.style.maxHeight = content.scrollHeight + 'px';
    }
  }
}

// Initialize all accordions
document.addEventListener('DOMContentLoaded', () => {
  const accordions = document.querySelectorAll('[data-accordion]');
  accordions.forEach(accordion => {
    new Accordion(accordion);
  });
});

// ===========================================
// CART AJAX FUNCTIONS
// ===========================================

const CartAPI = {
  /**
   * Add item to cart via AJAX
   * @param {number} variantId - Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @param {object} properties - Optional product properties
   */
  addToCart(variantId, quantity = 1, properties = {}) {
    const formData = {
      id: variantId,
      quantity: quantity,
      properties: properties
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to add item to cart');
        return response.json();
      })
      .then(data => {
        this.refreshCart();
        return data;
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        throw error;
      });
  },

  /**
   * Update cart item quantity
   * @param {string} key - Line item key
   * @param {number} quantity - New quantity
   */
  updateCartItem(key, quantity) {
    const formData = {
      line: key,
      quantity: quantity
    };

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update cart');
        return response.json();
      })
      .then(data => {
        this.refreshCart();
        return data;
      })
      .catch(error => {
        console.error('Error updating cart:', error);
        throw error;
      });
  },

  /**
   * Remove item from cart
   * @param {string} key - Line item key
   */
  removeCartItem(key) {
    return this.updateCartItem(key, 0);
  },

  /**
   * Refresh cart from server
   */
  refreshCart() {
    return fetch('/cart.js')
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
      })
      .then(data => {
        this.updateCartUI(data);
        this.updateItemCount(data.item_count);
        return data;
      })
      .catch(error => {
        console.error('Error fetching cart:', error);
      });
  },

  /**
   * Update cart drawer HTML with latest data
   * @param {object} cartData - Cart data from /cart.js
   */
  updateCartUI(cartData) {
    const drawer = document.querySelector('.cart-drawer');
    if (!drawer) return;

    const itemsContainer = drawer.querySelector('.cart-items-list');
    if (!itemsContainer) return;

    if (cartData.items.length === 0) {
      itemsContainer.innerHTML = '<p class="empty-message">Your cart is empty</p>';
      return;
    }

    itemsContainer.innerHTML = cartData.items.map(item => `
      <div class="cart-item" data-cart-key="${item.key}">
        <img src="${item.featured_image.url}" alt="${item.title}" class="cart-item-image" />
        <div class="cart-item-details">
          <h4>${item.title}</h4>
          <p class="cart-item-price">${this.formatPrice(item.price)}</p>
          <div class="cart-item-quantity">
            <button class="qty-decrease" data-key="${item.key}">−</button>
            <input type="number" min="0" value="${item.quantity}" class="qty-input" data-key="${item.key}" />
            <button class="qty-increase" data-key="${item.key}">+</button>
          </div>
        </div>
        <button class="cart-item-remove" data-key="${item.key}">✕</button>
      </div>
    `).join('');

    // Update totals
    const totalAmount = drawer.querySelector('.cart-total-amount');
    if (totalAmount) {
      totalAmount.textContent = this.formatPrice(cartData.total_price);
    }

    // Reattach event listeners
    this.attachCartItemListeners();
  },

  /**
   * Attach event listeners to cart items
   */
  attachCartItemListeners() {
    const drawer = document.querySelector('.cart-drawer');
    if (!drawer) return;

    // Quantity change listeners
    drawer.querySelectorAll('.qty-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const key = e.target.dataset.key;
        const quantity = parseInt(e.target.value);
        this.updateCartItem(key, quantity);
      });
    });

    drawer.querySelectorAll('.qty-increase').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.target.dataset.key;
        const input = drawer.querySelector(`.qty-input[data-key="${key}"]`);
        input.value = parseInt(input.value) + 1;
        input.dispatchEvent(new Event('change'));
      });
    });

    drawer.querySelectorAll('.qty-decrease').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.target.dataset.key;
        const input = drawer.querySelector(`.qty-input[data-key="${key}"]`);
        const newQuantity = Math.max(0, parseInt(input.value) - 1);
        input.value = newQuantity;
        input.dispatchEvent(new Event('change'));
      });
    });

    // Remove item listeners
    drawer.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.target.dataset.key;
        this.removeCartItem(key);
      });
    });
  },

  /**
   * Format price for display
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};

// Refresh cart on page load
document.addEventListener('DOMContentLoaded', () => {
  CartAPI.refreshCart();
});

// ===========================================
// WAIVER CHECKBOX HANDLER
// ===========================================

class WaiverCheckbox {
  constructor() {
    this.checkbox = document.getElementById('waiver-checkbox');
    this.checkoutBtn = document.getElementById('checkout-btn');

    this.init();
  }

  init() {
    if (!this.checkbox || !this.checkoutBtn) return;

    this.checkbox.addEventListener('change', () => this.toggleCheckout());
    this.updateCheckout();
  }

  toggleCheckout() {
    this.updateCheckout();
  }

  updateCheckout() {
    if (this.checkbox.checked) {
      this.checkoutBtn.disabled = false;
    } else {
      this.checkoutBtn.disabled = true;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new WaiverCheckbox();
});

// ===========================================
// SMOOTH SCROLL FOR ANCHOR LINKS
// ===========================================

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  });
});

// ===========================================
// INTERSECTION OBSERVER FOR FADE-IN ANIMATIONS
// ===========================================

class FadeInOnScroll {
  constructor() {
    this.elements = document.querySelectorAll('[data-fade-in]');
    this.init();
  }

  init() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    this.elements.forEach(el => observer.observe(el));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FadeInOnScroll();
});

// ===========================================
// QUANTITY BUTTON HANDLERS
// ===========================================

class QuantityButtons {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('qty-btn-increase')) {
        this.handleIncrease(e);
      } else if (e.target.classList.contains('qty-btn-decrease')) {
        this.handleDecrease(e);
      }
    });
  }

  handleIncrease(e) {
    const container = e.target.closest('[data-quantity-selector]');
    if (!container) return;

    const input = container.querySelector('input[type="number"]');
    if (input) {
      input.value = parseInt(input.value) + 1;
      input.dispatchEvent(new Event('change'));
    }
  }

  handleDecrease(e) {
    const container = e.target.closest('[data-quantity-selector]');
    if (!container) return;

    const input = container.querySelector('input[type="number"]');
    if (input) {
      const newValue = Math.max(0, parseInt(input.value) - 1);
      input.value = newValue;
      input.dispatchEvent(new Event('change'));
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new QuantityButtons();
});

// ===========================================
// PRODUCT VARIANT SELECTOR
// ===========================================

class ProductVariantSelector {
  constructor() {
    this.variantSelects = document.querySelectorAll('[data-variant-select]');
    this.init();
  }

  init() {
    this.variantSelects.forEach(select => {
      select.addEventListener('change', (e) => this.handleVariantChange(e));
    });
  }

  handleVariantChange(e) {
    const container = e.target.closest('[data-product-form]');
    if (!container) return;

    const variantId = e.target.value;
    if (!variantId) return;

    // Fetch variant data
    fetch(`/products/${container.dataset.productHandle}/variants/${variantId}.json`)
      .then(response => response.json())
      .then(variant => {
        // Update price display
        const priceElement = container.querySelector('[data-variant-price]');
        if (priceElement && variant.price) {
          priceElement.textContent = '$' + (variant.price / 100).toFixed(2);
        }

        // Update compare price if exists
        const comparePriceElement = container.querySelector('[data-variant-compare-price]');
        if (comparePriceElement && variant.compare_at_price) {
          comparePriceElement.textContent = '$' + (variant.compare_at_price / 100).toFixed(2);
          comparePriceElement.style.display = 'inline';
        } else if (comparePriceElement) {
          comparePriceElement.style.display = 'none';
        }

        // Update availability
        const availabilityElement = container.querySelector('[data-variant-availability]');
        if (availabilityElement) {
          availabilityElement.textContent = variant.available ? 'In Stock' : 'Out of Stock';
          availabilityElement.classList.toggle('unavailable', !variant.available);
        }

        // Update add to cart button
        const addBtn = container.querySelector('[data-add-to-cart]');
        if (addBtn) {
          addBtn.disabled = !variant.available;
        }
      })
      .catch(error => console.error('Error updating variant:', error));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductVariantSelector();
});

// ===========================================
// CART ITEM COUNT BADGE UPDATER
// ===========================================

class CartCountBadge {
  constructor() {
    this.badge = document.querySelector('[data-cart-count]');
    this.init();
  }

  init() {
    if (!this.badge) return;

    // Update on page load
    CartAPI.refreshCart();

    // Listen for custom cart update events
    document.addEventListener('cartUpdated', (e) => {
      this.update(e.detail.itemCount);
    });
  }

  update(count) {
    if (!this.badge) return;

    if (count > 0) {
      this.badge.textContent = count;
      this.badge.style.display = 'inline-block';
    } else {
      this.badge.style.display = 'none';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartCountBadge();
});

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

/**
 * Dispatch custom cart updated event
 * @param {number} itemCount - Total items in cart
 */
function dispatchCartUpdate(itemCount) {
  document.dispatchEvent(new CustomEvent('cartUpdated', {
    detail: { itemCount }
  }));
}

/**
 * Format price for display
 * @param {number} cents - Price in cents
 * @returns {string} Formatted price
 */
function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

/**
 * Debounce function helper
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
